
import React, { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { openEmailClient } from "@/utils/formHandlers";

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().optional(),
  message: z.string().min(5, { message: "Message must be at least 5 characters" }),
});

interface ContactFormProps {
  isVisible: boolean;
}

const ContactForm: React.FC<ContactFormProps> = ({ isVisible }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    
    try {
      // Create email content
      const subject = `New Contact Form Submission from ${values.name}`;
      const body = `
Name: ${values.name}
Email: ${values.email}
Phone: ${values.phone || 'Not provided'}

Message:
${values.message}

---
Sent from Presintention Contact Form
      `.trim();
      
      // Use mailto to open email client
      openEmailClient('Alex@presintention.com', subject, body);
      
      toast({
        title: "Email Client Opened",
        description: "Your email client should open with the message pre-filled. Please send the email to complete your submission.",
      });
      
      // Reset form after a short delay
      setTimeout(() => {
        form.reset();
      }, 1000);
      
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem preparing your email. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`transition-all duration-700 delay-400 ${isVisible ? 'opacity-100' : 'opacity-0 translate-x-8'}`}>
      <div className="bg-white border border-presintention-border rounded-xl p-8 shadow-sm">
        <h3 className="text-xl font-bold mb-6">Send Us a Message</h3>
        
        <form 
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-presintention-dark mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              {...form.register("name")}
              className="w-full px-4 py-3 rounded-md border border-presintention-border focus:outline-none focus:ring-2 focus:ring-presintention-accent"
              placeholder="Your name"
              required
            />
            {form.formState.errors.name && (
              <p className="text-red-500 text-sm mt-1">{form.formState.errors.name.message}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-presintention-dark mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                {...form.register("email")}
                className="w-full px-4 py-3 rounded-md border border-presintention-border focus:outline-none focus:ring-2 focus:ring-presintention-accent"
                placeholder="you@example.com"
                required
              />
              {form.formState.errors.email && (
                <p className="text-red-500 text-sm mt-1">{form.formState.errors.email.message}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-presintention-dark mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                {...form.register("phone")}
                className="w-full px-4 py-3 rounded-md border border-presintention-border focus:outline-none focus:ring-2 focus:ring-presintention-accent"
                placeholder="(555) 555-5555"
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-presintention-dark mb-1">
              Your Message <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              {...form.register("message")}
              rows={5}
              className="w-full px-4 py-3 rounded-md border border-presintention-border focus:outline-none focus:ring-2 focus:ring-presintention-accent"
              placeholder="How can we help you?"
              required
            ></textarea>
            {form.formState.errors.message && (
              <p className="text-red-500 text-sm mt-1">{form.formState.errors.message.message}</p>
            )}
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-presintention-accent text-white py-3 px-6 rounded-md hover:bg-opacity-90 transition-all button-transition font-medium disabled:opacity-70"
          >
            {isSubmitting ? "Preparing Email..." : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
